import React, { useState, useMemo, useEffect } from "react";
import {
  ModalWrapper,
  Button,
  ToggleSwitch,
  SignaturePad,
  Checklist,
} from "../../../components/ui";
import { useCoreContext } from "../../../context/global/CoreContext";
import { useRFIContext } from "../../../context/domain/operations/RFIContext";
import { createRFI, getNextRFIId, getRFIAttachmentFolderPath } from "../../../services/api/rfi";
import { filterJobsByDateRange } from "../../../utils/business/jobs";
import { getTodayInPT } from "../../../utils/datetime";
import {
  useTicketForm,
  useTicketAttachments,
  TicketAttachmentsSection,
} from "../../../features/tickets";

/**
 * CreateRFIModal - Modal form for creating a new RFI
 *
 * Uses: ModalWrapper from generic UI components
 * Uses: useCoreContext for job list
 * Uses: useRFIContext for refreshing list after create
 * Uses: createRFI from RFI API service
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback to close modal
 * @param {Function} onSuccess - Optional callback after successful creation
 */
const CreateRFIModal = ({ isOpen, onClose, onSuccess }) => {
  const { allJobsList } = useCoreContext();
  const { reportNewItem, users } = useRFIContext();

  // Use useTicketForm hook for form state management
  const {
    formData,
    setFormData,
    refs,
    handleChange: handleFormChange,
    getRef,
  } = useTicketForm({
    initialState: {
      job: "",
      date_performed: getTodayInPT(),
      cost_impact: false,
      schedule_impact: false,
      pm_signature: null,
    },
    initialRefs: {
      type: "r&s_form", // Default to R&S Form
      subject: "",
      description: "",
      location: "",
      spec_reference: "",
      drawing_reference: "",
      priority: "medium",
      required_by_date: "",
      assigned_to: "",
      scope_of_work: "",
      cost_impact_text: "",
      schedule_impact_text: "",
    },
    onSubmit: async () => {
      // This will be overridden by our custom handlers
    },
  });

  // State for UI-reactive values only (must trigger re-renders)
  const [isSubmitting, setIsSubmitting] = useState(false); // Button text/disabled state
  const [error, setError] = useState(null); // Error message display
  const [rfiNumber, setRfiNumber] = useState(""); // Displayed in read-only input
  const [projectNumber, setProjectNumber] = useState(""); // Displayed in input field
  const [isLoadingRFIId, setIsLoadingRFIId] = useState(false); // Placeholder text
  // Required-field state for reactive checklist (subject/description live in refs)
  const [subjectValue, setSubjectValue] = useState("");
  const [questionValue, setQuestionValue] = useState("");

  // Attachments (create flow: no ticketId until after create; upload after create via override)
  const {
    attachments,
    pendingFiles,
    setPendingFiles,
    isUploadingFiles,
    handleUploadAttachments,
  } = useTicketAttachments({
    ticketId: null, // No RFI yet; upload happens in handleSubmitRFI with created rfi_id
    ticketIdentifier: rfiNumber,
    getAttachmentFolderPathApi: getRFIAttachmentFolderPath,
    uploadAction: "upload_rfi_attachment",
    uploadAdditionalData: rfiNumber ? { rfi_id: rfiNumber } : undefined,
    coverPageLabel: null,
  });

  // Filter jobs to only show active ones (ended within last year)
  const activeJobsList = useMemo(() => {
    return filterJobsByDateRange(allJobsList);
  }, [allJobsList]);

  // Get selected job object
  const selectedJob = useMemo(() => {
    return allJobsList?.find((job) => job.id === parseInt(formData.job));
  }, [allJobsList, formData.job]);

  // Fetch RFI Number and set Contract Number when project is selected
  useEffect(() => {
    const fetchRFIId = async () => {
      if (!formData.job || !selectedJob) {
        setRfiNumber("");
        setProjectNumber("");
        return;
      }

      // Auto-fill contract number (project_number) from selected job
      setProjectNumber(selectedJob.project_number || "");

      setIsLoadingRFIId(true);
      try {
        const response = await getNextRFIId(selectedJob.number);
        setRfiNumber(response.rfi_id || "");
      } catch (err) {
        // Only log if it's not a connection error (backend might not be running)
        if (err.code !== 'ERR_NETWORK' && err.code !== 'ERR_CONNECTION_REFUSED') {
          console.error("Failed to fetch RFI ID:", err);
        }
        setRfiNumber("");
      } finally {
        setIsLoadingRFIId(false);
      }
    };

    fetchRFIId();
  }, [formData.job, selectedJob]);

  // Handle form field changes (wraps useTicketForm's handleChange to clear errors)
  const handleChange = (e) => {
    const { name, value } = e.target || {};
    if (name === "subject") setSubjectValue(value);
    if (name === "description") setQuestionValue(value);
    handleFormChange(e);
    setError(null);
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      job: "",
      date_performed: getTodayInPT(),
      cost_impact: false,
      schedule_impact: false,
      pm_signature: null,
    });
    // Reset refs to initial values
    refs.current = {
      type: "r&s_form",
      subject: "",
      description: "",
      location: "",
      spec_reference: "",
      drawing_reference: "",
      priority: "medium",
      required_by_date: "",
      assigned_to: "",
      scope_of_work: "",
      cost_impact_text: "",
      schedule_impact_text: "",
    };
    setRfiNumber("");
    setProjectNumber("");
    setError(null);
    setPendingFiles([]);
    setSubjectValue("");
    setQuestionValue("");
  };

  // Handle modal close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Required fields for create: Project, Subject, Question
  const canSubmit =
    !!formData.job &&
    subjectValue.trim().length > 0 &&
    questionValue.trim().length > 0;

  const createChecklistConditions = [
    { met: !!formData.job, message: "Please select a Project." },
    {
      met: subjectValue.trim().length > 0,
      message: "Please enter a Subject.",
    },
    {
      met: questionValue.trim().length > 0,
      message: "Please enter a Question.",
    },
  ];

  // Validate form (used on submit; refs are source of truth for payload)
  const validateForm = () => {
    if (!formData.job) {
      setError("Please select a project");
      return false;
    }
    if (!getRef("subject")?.trim()) {
      setError("Please enter a subject");
      return false;
    }
    if (!getRef("description")?.trim()) {
      setError("Please enter a question");
      return false;
    }
    return true;
  };

  // Handle form submission (Submit RFI)
  const handleSubmitRFI = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        ...refs.current,
        job: parseInt(formData.job),
        rfi_id: rfiNumber?.trim() || undefined,
      };

      // Clean up empty optional fields
      if (!payload.location) delete payload.location;
      if (!payload.spec_reference) delete payload.spec_reference;
      if (!payload.drawing_reference) delete payload.drawing_reference;
      if (!payload.required_by_date) delete payload.required_by_date;
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.scope_of_work) delete payload.scope_of_work;
      if (!payload.cost_impact_text) delete payload.cost_impact_text;
      if (!payload.schedule_impact_text) delete payload.schedule_impact_text;

      // Create RFI first
      const createdRFI = await createRFI(payload);

      // If there are pending files, upload them now that we have the RFI ID (shared hook with override)
      if (pendingFiles.length > 0 && createdRFI?.rfi_id) {
        try {
          await handleUploadAttachments({ rfi_id: createdRFI.rfi_id });
        } catch (uploadErr) {
          console.error("Failed to upload attachments:", uploadErr);
          setError(
            "RFI created successfully, but some attachments failed to upload. You can add them from the RFI detail page."
          );
        }
      }
      await reportNewItem(createdRFI?.id);

      handleClose();
      onSuccess?.();
    } catch (err) {
      console.error("Failed to submit RFI:", err);
      setError(
        err.message ||
        "Failed to submit RFI. Please check your connection and try again."
      );
      // Modal remains open on error - user can retry when they have better signal
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isUploadingFiles ? (
        <ModalWrapper isOpen={isOpen} onClose={() => { }} closeOnOutsideClick={false} closeButton={false}>
          <div className="p-2 caret-transparent">
            <div className="flex items-center justify-center gap-3 p-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Uploading Attachments
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Please wait while your files are being uploaded...
                </p>
              </div>
            </div>
          </div>
        </ModalWrapper>
      ) : (
        <ModalWrapper
          isOpen={isOpen}
          onClose={handleClose}
          closeOnOutsideClick={false}
          className="mt-[48px] min-h-[calc(100vh-48px)] md:min-h-0 bg-white md:rounded-md shadow-lg p-4 relative w-full md:max-w-[680px] md:w-auto max-h-[90vh] overflow-y-auto"
        >
          <div className="p-2 md:p-4">
            {/* Header */}
            <h2 className="text-xl font-bold text-gray-900 mb-1">Create New RFI</h2>
            <p className="text-sm text-gray-500 mb-4">
              Request for Information - Document a question or issue that needs
              resolution
            </p>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Form */}
            <div className="space-y-4">
              {/* Job Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project <span className="text-red-500">*</span>
                </label>
                <select
                  name="job"
                  value={formData.job}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select a job...</option>
                  {activeJobsList?.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.number} - {job.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* RFI Number and Contract Number - Single column on mobile, two columns on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    RFI Number
                  </label>
                  <input
                    type="text"
                    value={rfiNumber}
                    onChange={(e) => setRfiNumber(e.target.value)}
                    placeholder={
                      isLoadingRFIId ? "Loading..." : "Select project to generate"
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contract Number
                  </label>
                  <input
                    type="text"
                    value={projectNumber}
                    readOnly
                    placeholder={
                      formData.job && !projectNumber
                        ? "Not set in admin panel"
                        : "Select project to auto-fill"
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Horizontal divider */}
              <div className="border-b border-gray-200 my-4"></div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  name="type"
                  defaultValue="r&s_form"
                  data-ref="true"
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select type...</option>
                  <option value="r&s_form">R&S Form</option>
                  <option value="city_form">City Form</option>
                </select>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  defaultValue=""
                  data-ref="true"
                  onChange={handleChange}
                  placeholder="Brief description of the question"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength={255}
                  required
                />
              </div>

              {/* Question */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  defaultValue=""
                  data-ref="true"
                  onChange={handleChange}
                  placeholder="Detailed question or information being requested..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  required
                />
              </div>

              {/* Reference Fields - Location, Spec Reference, Drawing Reference - Single column on mobile, three columns on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    defaultValue=""
                    data-ref="true"
                    onChange={handleChange}
                    placeholder="e.g., Grid A-3, Level 2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Spec Reference
                  </label>
                  <input
                    type="text"
                    name="spec_reference"
                    defaultValue=""
                    data-ref="true"
                    onChange={handleChange}
                    placeholder="Spec reference"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Drawing Reference
                  </label>
                  <input
                    type="text"
                    name="drawing_reference"
                    defaultValue=""
                    data-ref="true"
                    onChange={handleChange}
                    placeholder="e.g., S-101, Detail 3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Priority and Due Date - Single column on mobile, two columns on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    name="priority"
                    defaultValue="medium"
                    data-ref="true"
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Required By Date
                  </label>
                  <input
                    type="date"
                    name="required_by_date"
                    defaultValue=""
                    data-ref="true"
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Assign To and Scope of Work - Single column on mobile, two columns on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assign To
                  </label>
                  <select
                    name="assigned_to"
                    defaultValue=""
                    data-ref="true"
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select team member</option>
                    {users?.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first} {user.last}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Scope of Work
                  </label>
                  <input
                    type="text"
                    name="scope_of_work"
                    defaultValue=""
                    data-ref="true"
                    onChange={handleChange}
                    placeholder="e.g., Foundation & Concrete"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Impact Toggles */}
              <div className="space-y-4">
                {/* Cost Impact Toggle */}
                <div>
                  <ToggleSwitch
                    text="Potential Cost Impact"
                    isOn={formData.cost_impact}
                    handleToggle={() =>
                      setFormData((prev) => ({
                        ...prev,
                        cost_impact: !prev.cost_impact,
                        cost_impact_text: prev.cost_impact
                          ? ""
                          : prev.cost_impact_text, // Clear text when toggled off
                      }))
                    }
                  />
                  {formData.cost_impact && (
                    <div className="mt-2">
                      <textarea
                        name="cost_impact_text"
                        defaultValue=""
                        data-ref="true"
                        onChange={handleChange}
                        placeholder="Describe the potential cost impact..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    </div>
                  )}
                </div>

                {/* Schedule Impact Toggle */}
                <div>
                  <ToggleSwitch
                    text="Potential Schedule Impact"
                    isOn={formData.schedule_impact}
                    handleToggle={() =>
                      setFormData((prev) => ({
                        ...prev,
                        schedule_impact: !prev.schedule_impact,
                        schedule_impact_text: prev.schedule_impact
                          ? ""
                          : prev.schedule_impact_text, // Clear text when toggled off
                      }))
                    }
                  />
                  {formData.schedule_impact && (
                    <div className="mt-2">
                      <textarea
                        name="schedule_impact_text"
                        defaultValue=""
                        data-ref="true"
                        onChange={handleChange}
                        placeholder="Describe the potential schedule impact..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Attachments (shared ticket section; upload on submit) */}
              <TicketAttachmentsSection
                ticketIdentifier={rfiNumber}
                coverSubtitle={refs.current?.type === "city_form" ? "City RFI Form" : "R&S RFI Form"}
                attachments={attachments}
                handleDragEnd={() => { }}
                pendingFiles={pendingFiles}
                pendingFilesHelpText={formData.job ? "Files will be uploaded when RFI is created." : "Please select a project first."}
                setPendingFiles={setPendingFiles}
                isLoadingAttachments={false}
                isUploadingFiles={isUploadingFiles}
                handleUploadAttachments={() => { }}
                disabled={!formData.job || isSubmitting || isUploadingFiles}
                onUploadError={() => setError("Failed to select files. Please try again.")}
                showUploadButton={false}
                showPdfPagesSection={false}
                showNotCreatedMessage={true}
              />

              {/* Project Manager Signature */}
              <div className="border-b pb-4 mt-6">
                <SignaturePad
                  value={formData?.pm_signature}
                  onChange={(signature) =>
                    setFormData((prev) => ({ ...prev, pm_signature: signature }))
                  }
                  readOnly={false}
                  height={156}
                />
              </div>
            </div>

            {/* Required fields checklist */}
            <Checklist
              className="w-full mt-4"
              conditions={createChecklistConditions}
              successMessage="All required fields are complete. You can submit the RFI."
            />

            {/* Action Buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                variant="secondary"
                text="Cancel"
                onClick={handleClose}
                disabled={isSubmitting}
                className="w-full flex-1"
              />
              <Button
                variant="primary"
                text={
                  isSubmitting
                    ? isUploadingFiles
                      ? "Submitting & Uploading..."
                      : "Submitting..."
                    : "Submit RFI"
                }
                onClick={handleSubmitRFI}
                disabled={isSubmitting || !canSubmit}
                className="w-full flex-1"
              />
            </div>
          </div>
        </ModalWrapper>
      )}
    </>
  );
};

export default CreateRFIModal;
